import { Component, ChangeDetectionStrategy, computed, input } from '@angular/core';

/**
 * Spinner présentationnel du kit interne, pour un usage local : section en
 * cours de chargement, requête longue déclenchée à la main, contenu différé.
 * La barre globale (idra-loading-bar) reste préférable pour les transitions
 * de pages et les requêtes automatiques.
 *
 * Couleur héritée de `currentColor` par défaut — poser une couleur sur le
 * parent la propage ; passer `primary` force la couleur primaire.
 *
 * Usage :
 *  <idra-spinner />                                  <!-- 20px, inline -->
 *  <idra-spinner [size]="40" label="Chargement…" />
 *  <idra-spinner [size]="32" center primary />      <!-- centré, couleur primaire -->
 */
@Component({
  selector: 'idra-spinner',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.center]': 'center()',
    '[class.primary]': 'primary()',
    role: 'status',
    '[attr.aria-label]': 'label() || "Chargement en cours"',
    '[attr.aria-live]': '"polite"',
  },
  styles: `
    :host {
      display: inline-flex;
      align-items: center;
      gap: 10px;
      color: inherit;
    }

    :host(.center) {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      width: 100%;
      padding: 24px 0;
    }

    :host(.primary) {
      color: var(--idra-primary);
    }

    .ring {
      flex: none;
      display: inline-block;
      width: var(--spinner-size);
      height: var(--spinner-size);
      border: var(--spinner-thickness) solid var(--idra-border);
      border-top-color: currentColor;
      border-radius: var(--idra-radius-full);
      animation: idra-spin 0.7s linear infinite;
    }

    .label {
      font-size: 14px;
      color: var(--idra-text-secondary);
    }

    @keyframes idra-spin {
      to {
        transform: rotate(360deg);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .ring {
        animation-duration: 1.6s;
      }
    }
  `,
  template: `
    <span class="ring" aria-hidden="true" [style]="ringStyle()"></span>
    @if (label()) {
      <span class="label">{{ label() }}</span>
    }
  `,
})
export class IdraSpinner {
  /** Diamètre du spinner en pixels. */
  readonly size = input(20);

  /** Libellé optionnel affiché à côté du spinner. */
  readonly label = input<string | undefined>(undefined);

  /** Centre le spinner (et son libellé) dans son conteneur. */
  readonly center = input(false);

  /** Force la couleur primaire (sinon hérite de currentColor). */
  readonly primary = input(false);

  /** Épaisseur du trait, proportionnelle à la taille (bornée). */
  protected readonly ringStyle = computed(() => {
    const size = this.size();
    const thickness = Math.max(2, Math.round(size / 8));
    return `--spinner-size:${size}px;--spinner-thickness:${thickness}px`;
  });
}
